import { ReactNode, useEffect, useState } from "react";
import { request } from "components/utils/Request2";
import { Box, CircularProgress, Drawer, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { localeAsLanguageKey, locales } from "utils/locales";
import { showFile } from "components/filepreview/FilePreview";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { saveAs } from "file-saver";
import { intl } from "LocaleWrapper";
import { Button, RequireProps } from "@cofano/cck";
import { buildDocumentUrl, extractFilenameFromContentDispositionHeader } from "components/documents/DocumentsUtils";
import { Language } from "utils";

type DocumentDrawerProps = {
	open: boolean,
	url: string,
	previewUrl?: string,
	queryParams?: Record<string, string | boolean | number | undefined>,
	onClose: () => void,
	multilingual?: boolean
	downloadedFileName?: string,
	actions?: ReactNode[]
};

const DocumentDrawer = ({ blob = false, ...rest }: {
	blob?: boolean,
} & DocumentDrawerProps) => {
	return blob ? (
		<BlobDocumentDrawer {...rest} />
	) : (
		<JsonDocumentDrawer {...rest} />
	);
};

const BlobDocumentDrawer = ({ onClose, ...params }: DocumentDrawerProps) => {
	const { instance } = useCoastguard();

	const [ preview, setPreview ] = useState<{ content: string; type: string } | null>(null);
	const [ language, setLanguage ] = useState<string | undefined>(params.multilingual ? "en" : undefined);
	const [ loading, setLoading ] = useState(false);

	const loadPreview = (language: string | undefined) => {
		setPreview(null);
		setLoading(true);

		instance.fetchBlob(buildDocumentUrl(params.url, params.queryParams, language, true, true), {
			method: "GET",
			headers: {
				"Accept": "application/pdf, image/*, application/octet-stream",
			},
		}).then(response => {
			setPreview({
				content: URL.createObjectURL(response),
				type: response.type,
			});
		}).catch(() => {
			setPreview(null);
		}).finally(() => setLoading(false));
	};

	return (
		<BaseDocumentDrawer
			{...params}
			loading={loading}
			language={language}
			setLanguage={setLanguage}
			loadPreview={loadPreview}
			onClose={() => {
				onClose?.();
				setPreview(null);
			}}
		>
			{!preview || loading ? (
				<div style={{ display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "center" }}>
					<CircularProgress size={24} />
				</div>
			) : preview.type.includes("pdf") ? (
				<iframe
					src={preview.content}
					title={intl.formatMessage({ id: "TEMPLATE_PREVIEW" })}
					style={{ alignSelf: "stretch", flexGrow: 1, minHeight: 500 }}
				/>
			) : preview.type.includes("image") ? (
				<img
					alt={intl.formatMessage({ id: "TEMPLATE_PREVIEW" })}
					src={preview.content}
					style={{ objectFit: "contain", maxWidth: "100%", maxHeight: "100%", width: "100%" }}
				/>
			) : (
				<>{Language("FILE_TYPE_NO_PREVIEW")}</>
			)}
		</BaseDocumentDrawer>
	);
};

const JsonDocumentDrawer = ({ onClose, ...params }: DocumentDrawerProps) => {
	const [ preview, setPreview ] = useState<{ base64: string, type: string } | null>(null);
	const [ language, setLanguage ] = useState<string | undefined>(params.multilingual ? "en" : undefined);
	const [ loading, setLoading ] = useState(false);

	const loadPreview = (language: string | undefined) => {
		setPreview(null);
		setLoading(true);

		request<Record<string, string>>({
			url: buildDocumentUrl(params.previewUrl ?? params.url, params.queryParams, language, true, false),
			method: "GET",
		}).then(response => {
			const entries = Object.entries(response);
			if (entries.length !== 1) {
				throw new Error("Invalid response. Document response must have only one key-value pair.");
			}
			for (const [ key, value ] of entries) {
				setPreview({ base64: value, type: key });
			}
		}).finally(() => setLoading(false));
	};

	return (
		<BaseDocumentDrawer
			{...params}
			loading={loading}
			language={language}
			setLanguage={setLanguage}
			loadPreview={loadPreview}
			onClose={() => {
				onClose?.();
				setPreview(null);
			}}
		>
			{preview && !loading ? showFile(preview.base64, preview.type) : (
				<div style={{ display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "center" }}>
					<CircularProgress size={24} />
				</div>
			)}
		</BaseDocumentDrawer>
	);
};

const BaseDocumentDrawer = ({ open, onClose, url, queryParams, multilingual, downloadedFileName, actions, loadPreview, language, setLanguage, children, loading }: {
	loadPreview: (language: string | undefined) => void,
	language: string | undefined,
	setLanguage: (language: string | undefined) => void,
	children: ReactNode,
	loading: boolean,
} & RequireProps<DocumentDrawerProps, "onClose">) => {

	const { instance } = useCoastguard();
	const [ downloading, setDownloading ] = useState(false);

	const download = () => {
		setDownloading(true);

		instance.fetch(buildDocumentUrl(url, queryParams, language, false, true), {
			method: "GET",
			headers: {
				"Accept": "application/pdf, image/*, application/octet-stream",
			},
		}).then(async (response) => {
			const filename = downloadedFileName ?? extractFilenameFromContentDispositionHeader(response.headers);
			saveAs(await response.blob(), filename ?? undefined);
		}).finally(() => {
			setDownloading(false);
		});
	};

	useEffect(() => {
		if (open) {
			loadPreview(language);
		}
	}, [ open ]);

	return (
		<Drawer
			open={open}
			anchor="right"
			style={{ zIndex: 1301 }}
			onClose={() => {
				onClose();
			}}
			PaperProps={{
				sx: {
					maxWidth: "80%",
					minWidth: "50vw",
				},
			}}
		>
			<div style={{ margin: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<div>
					{multilingual && (
						<RadioGroup
							value={language}
							onChange={event => {
								const languageCode = event.target.value;
								setLanguage(languageCode);
								loadPreview(languageCode);
							}}
							row
						>
							{Object.values(locales).map((locale) => (
								<FormControlLabel
									key={locale}
									value={locale}
									control={<Radio />}
									label={localeAsLanguageKey(locale)}
									disabled={loading}
								/>
							))}
						</RadioGroup>
					)}
				</div>
				<div className="actions" style={{ display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "end" }}>
					<div>
						{actions && actions.map((action, i) => (
							<div key={i}>
								{action}
							</div>
						))}
					</div>
					<Button
						onClick={() => download()}
						loading={downloading}
						disabled={downloading || loading}
						size="small"
						variant="outlined"
					>
						{intl.formatMessage({ id: "DOWNLOAD" })}
					</Button>
				</div>
			</div>
			<Box className="preview" sx={{ marginX: 2, display: "flex", flexGrow: 1 }}>
				{children}
			</Box>
		</Drawer>
	);
};

export default DocumentDrawer;
